import { useCallback, useContext } from 'react'

import useSetImmersiveMode from './useImmersiveModeHandlers'
import useTripClientSource from './useTripClientSource'
import BookHotelFlights from '../components/TripModal/RestoreCart/BookHotelFlight'

import { getCartItems } from 'api/cart'
import { getRestoreCartUriForId } from 'checkout/lib/utils/misc/restoreCartUtils'
import { BaseButtonProps } from 'components/Luxkit/Button/BaseButton'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useNavigateTo from 'hooks/useNavigateTo'
import noop from 'lib/function/noop'
import { getCurrentUserId } from 'selectors/accountSelectors'
import { ItemCardVariant } from 'tripPlanner/components/ItemCard/ItemCard'
import BookTourFlight from 'tripPlanner/components/TripModal/RestoreCart/BookTourFlight'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useExperience, useOffer } from 'tripPlanner/hooks/api'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { LETripItem } from 'tripPlanner/types/tripItem'
import { getRestoreCartButtonCopy } from 'tripPlanner/utils/bookmark/buttonCopy'
import {
  buildProductLinkWithQueryParams,
  isBookableHotelFlightBundle,
  isBookableTourV1FlightBundle,
} from 'tripPlanner/utils/bookmark/restoreCart'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

export const BOOK_NOW_BUTTON_TEXT = 'Book now'

type ReturnProps = {
  label: string | undefined
  onClick: Exclude<BaseButtonProps['onClick'], undefined>
  productLink: string | undefined
  canRestoreCart: boolean
}

const offerSourceTypes = new Set<LETripItem['sourceType']>([
  'LE_Hotel',
  'LE_Hotel_Bedbank',
  'LE_Tour',
  'LE_Cruise',
  'LE_Accommodation_Rental',
])

export default function useRestoreCartButtonProps(
  item: LETripItem,
  variant: ItemCardVariant,
): ReturnProps {
  let offerId: string | undefined = undefined
  if (offerSourceTypes.has(item.sourceType)) {
    offerId = item.code
  } else if (item.type === 'FLIGHT' && item.savedItemData?.hotelBundle) {
    offerId = item.savedItemData.hotelBundle.offerId
  } else if (item.type === 'FLIGHT' && item.savedItemData?.tourBundle) {
    offerId = item.savedItemData.tourBundle.offerId
  }
  const { data: offer } = useOffer(offerId, {
    isCruise: item.sourceType === 'LE_Cruise',
  })
  const { data: experience } = useExperience({
    currentCurrency: item.savedItemData.currencyCode,
    currentRegionCode: item.savedItemData.regionCode,
    experienceId: item.sourceType === 'LE_Experience' ? item.code : undefined,
  })

  const currentUserId = useAppSelector(getCurrentUserId)

  const tripContext = useContext(TripContext)
  const viewAsTemplate = tripContext?.viewAsTemplate
  const canEdit = tripContext?.permissions.canEdit

  const { setImmersiveMode } = useSetImmersiveMode()
  const openModal = useOpenModal()

  const source = useTripClientSource()

  const productLink = buildProductLinkWithQueryParams(
    item,
    !viewAsTemplate,
    { offer, experience },
    { source },
  )

  const navigateTo = useNavigateTo()

  const modalElement = useModalElementContext()
  const onClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const canBookOfferStandalone =
        offer && !(offer as App.Offer).bundledWithFlightsOnly

      if (item.savedItemData.cartId) {
        let canRestoreCart = false
        await getCartItems(item.savedItemData.cartId)
          .then(() => {
            canRestoreCart = true
          })
          .catch(noop)

        if (canRestoreCart) {
          const restoreCartUrl = getRestoreCartUriForId(
            item.savedItemData.cartId,
          )

          if (isBookableHotelFlightBundle(item) && canBookOfferStandalone) {
            openModal(BookHotelFlights, { item, restoreCartUrl })
          } else if (
            isBookableTourV1FlightBundle(item) &&
            canBookOfferStandalone
          ) {
            openModal(BookTourFlight, { item, restoreCartUrl })
          } else {
            navigateTo(restoreCartUrl)
          }
          return
        }
      }

      if (productLink) {
        if (canEdit) {
          setImmersiveMode({
            tripId: item.tripId,
            sectionId: itemSectionId(item.id),
          })
        }

        navigateTo(productLink)
      }
      modalElement?.resolve()
    },
    [
      canEdit,
      item,
      modalElement,
      navigateTo,
      offer,
      openModal,
      productLink,
      setImmersiveMode,
    ],
  )

  const label =
    variant === 'drawer' ? BOOK_NOW_BUTTON_TEXT : getRestoreCartButtonCopy(item)

  const canRestoreCart =
    !!productLink &&
    !!item.savedItemData.cartId &&
    !!label &&
    currentUserId === item.createdBy

  if (!canRestoreCart) {
    return {
      label: undefined,
      canRestoreCart: false,
      onClick: noop,
      productLink: undefined,
    }
  }

  return {
    label,
    canRestoreCart: true,
    onClick,
    productLink,
  }
}
